import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { listVariables } from '@services/sm-api.js'
import CopyToClipboard from '@shared/CopyToClipboard'

import V2Layout from '../V2Layout'
import V2Stepper from '../V2Stepper'
import carritoSucesso from '../assets/carrito-sucesso.png'

// Conclusão V2 — porte fiel do design (isStep6). Regras preservadas (MATRIZ):
// D-02 (título por billet_url), D-03 (fallback sem order_id — refresh/acesso direto),
// D-04 (matriz de mensagem billet × b2c), D-05 (chave PIX copiável no híbrido),
// D-07 (textos do boleto híbrido), D-08 (download do boleto + GA baixou_boleto_5.1),
// D-10 (HYBRID_BILLET_ACTIVATION relida na tela), D-11 (dados de state.creditCard).

const money = v => Number(v || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })
const cell = { display: 'block', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B6664' }

export default function V2Concluido() {
  const order = useSelector(state => state.creditCard || {})
  const [hybrid, setHybrid] = useState(false)

  useEffect(() => {
    // D-10: flag relida na própria tela
    listVariables()
      .then(vars => {
        const v = Array.isArray(vars) && vars.find(x => x.key === 'HYBRID_BILLET_ACTIVATION')
        setHybrid(!!v && v.value === '1')
      })
      .catch(() => {})
  }, [])

  const hasOrder = !!order.order_id
  const hasBillet = !!order.billet_url
  const isB2C = !!order.is_b2c
  // fallback: resumo leve gravado pela etapa de Resumo (a resposta da API não traz
  // totais/aluno e o carrinho é limpo no sucesso — D-13)
  let summary = {}
  try {
    summary = JSON.parse(sessionStorage.getItem('smd_v2_order_summary') || '{}')
  } catch (e) {}
  const student = (order.voucher_data && order.voucher_data[0]) || { name: summary.name, voucher: summary.voucher }
  const orderTotal = order.order_total || summary.total
  // pedido de negócio (09/09, itens 9/10): todos os alunos com voucher e nº de itens
  const fromApi = Array.isArray(order.voucher_data) ? order.voucher_data.filter(v => v && v.voucher) : []
  const fromSummary = Array.isArray(summary.list) ? summary.list : []
  const students = (fromApi.length >= fromSummary.length ? fromApi : fromSummary).map(v => {
    const extra = fromSummary.find(x => x.voucher === v.voucher) || {}
    return { name: v.name || extra.name, voucher: v.voucher, items: extra.items }
  })
  if (!students.length && student.name) students.push({ name: student.name, voucher: student.voucher })
  const METHOD_NAMES = { billet: hybrid ? 'Boleto/PIX' : 'Boleto', card: 'Cartão de crédito', pix: 'PIX', twocards: 'Dois cartões' }
  const methodName = hasBillet ? METHOD_NAMES.billet : METHOD_NAMES[summary.method] || null

  // D-04: matriz de mensagem
  let message
  if (!hasBillet) {
    message = isB2C
      ? 'Agora é só aguardar. Seu pedido será enviado para o Endereço de Entrega preenchido no ato da compra. A nota fiscal será enviada para o e-mail (verifique caixa de SPAM e lixo eletrônico).'
      : 'Agora é só aguardar a entrega na escola. A nota fiscal será enviada para o seu e-mail.'
  } else {
    message = isB2C
      ? 'Seu pedido será enviado para o Endereço de Entrega preenchido no ato da compra, após a confirmação do pagamento. A nota fiscal será enviada para o e-mail (verifique caixa de SPAM e lixo eletrônico).'
      : 'Seu pedido será enviado para a escola após a confirmação do pagamento e a nota fiscal será enviada para o seu email.'
  }

  const _downloadBillet = () => {
    // D-08: GA + nova aba
    window.ga && window.ga('send', { hitType: 'event', eventCategory: 'compra', eventAction: 'click', eventLabel: 'baixou_boleto_5.1' })
    window.open(order.billet_url, '_blank')
  }

  // "O que acontece agora": só passos que o sistema realmente cumpre, sem prazos inventados
  const delivered = isB2C || summary.isB2C
  const nextSteps = hasBillet
    ? [
        { t: hybrid ? 'Pague o boleto ou o PIX' : 'Pague o boleto', d: 'O pedido só segue depois do pagamento.', on: true },
        { t: 'Confirmação do pagamento', d: 'A compensação do boleto pode levar até 3 dias úteis.' },
        delivered
          ? { t: 'Envio ao seu endereço', d: 'O rastreio aparece em Minha conta quando o pedido sair com a transportadora.' }
          : { t: 'Entrega na escola', d: 'A escola informa a data de entrega do material.' },
      ]
    : [
        { t: 'Pedido recebido', d: 'Você recebe a confirmação no e-mail da compra.', done: true },
        { t: 'Preparação do pedido', d: 'Acompanhe o andamento em Minha conta.' },
        delivered
          ? { t: 'Envio ao seu endereço', d: 'O rastreio aparece em Minha conta quando o pedido sair com a transportadora.' }
          : { t: 'Entrega na escola', d: 'A escola informa a data de entrega do material.' },
      ]

  return (
    <V2Layout>
      <V2Stepper current={4} />
      <section className="v2-done v2-done-section">
        <div className="v2-done__top">
          {/* Carrito de sucesso só quando o pedido está de fato concluído (nunca em pagamento pendente) */}
          {!hasBillet && hasOrder ? <img className="v2-done__carrito" src={carritoSucesso} alt="" aria-hidden="true" /> : null}
          <div className="v2-done__copy">
            <span className={`v2-done__pill${hasBillet ? ' is-wait' : ''}`}>{hasBillet ? 'Aguardando pagamento' : 'Pedido recebido'}</span>
            {/* D-02: título por situação do pagamento */}
            <h1>{hasBillet ? 'Pagamento pendente' : 'Pedido concluído'}</h1>
            {!hasOrder ? (
              // D-03: acesso direto/refresh — dados do pedido não persistem
              <p>Acesse sua conta para acompanhar o pedido.</p>
            ) : (
              <p>{message}</p>
            )}
            <div className="v2-done__actions">
              {/* D-08: download do boleto */}
              {hasOrder && hasBillet && (
                <button id="v2-done-billet" type="button" className="v2-btn v2-btn--primary" onClick={_downloadBillet}>
                  {hybrid ? 'Visualizar boleto/PIX' : 'Baixar boleto'}
                </button>
              )}
              <a href="/v2/conta/pedidos" className={`v2-btn ${hasBillet ? 'v2-btn--neutral' : 'v2-btn--primary'}`}>
                {hasOrder ? 'Acompanhar o pedido' : 'Ir para a minha conta'}
              </a>
              <a href="/" className="v2-btn v2-btn--neutral">Comprar para outro aluno</a>
            </div>
          </div>

          {hasOrder && (
            <div id="v2-done-summary" className="v2-done__card">
              <span style={cell}>Número do pedido</span>
              <strong className="v2-done__number" id="v2-done-order">{order.order_id}</strong>
              <dl>
                <div><dt>Total</dt><dd>{orderTotal ? money(orderTotal) : '—'}</dd></div>
                {methodName && <div><dt>Pagamento</dt><dd>{methodName}</dd></div>}
                <div><dt>Entrega</dt><dd>{delivered ? 'No endereço informado' : 'Na escola'}</dd></div>
                {summary.estimate && <div><dt>Estimativa</dt><dd>{summary.estimate}</dd></div>}
              </dl>
              <span style={{ ...cell, marginTop: '14px' }}>{students.length > 1 ? 'Alunos' : 'Aluno'}</span>
              {students.map((st, i) => (
                <div key={`${st.voucher}-${i}`} className="v2-done-student">
                  <span>
                    <b>{st.name || '—'}</b>
                    {st.items ? <small>{st.items} {st.items === 1 ? 'item' : 'itens'}</small> : null}
                  </span>
                  <em>Voucher {st.voucher || '—'}</em>
                </div>
              ))}
            </div>
          )}
        </div>

        {hasOrder && (
          <React.Fragment>
            {/* D-05: chave PIX copiável (boleto híbrido) */}
            {hasBillet && hybrid && order.qrcode && (
              <div className="v2-done__pix">
                <strong>Copie a chave PIX e realize o pagamento no seu internet banking</strong>
                <CopyToClipboard text={order.qrcode} />
              </div>
            )}
            {/* D-07: orientações do boleto híbrido */}
            {hasBillet && hybrid && (
              <p className="v2-done__note">
                Caso queira pagar mais tarde, você poderá baixar o boleto em "Visualizar boleto/Pix". Nele contém o
                código de barras ou QR code para pagamento por PIX. Você também poderá acessar o boleto em "Pedidos",
                acessando sua conta no SM Direto.
              </p>
            )}

            <h2 className="v2-done__h2">O que acontece agora</h2>
            <ol className="v2-done__steps">
              {nextSteps.map((st, i) => (
                <li key={st.t} className={st.done ? 'is-done' : st.on ? 'is-on' : ''}>
                  <span>{st.done ? '✓' : i + 1}</span>
                  <strong>{st.t}</strong>
                  <p>{st.d}</p>
                </li>
              ))}
            </ol>
          </React.Fragment>
        )}
      </section>
    </V2Layout>
  )
}
